import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl, formatApiError } from "../lib/api";
import { GAME_MODES, CHEAT_TYPES, videoHostAllowed } from "../lib/constants";
import { uploadVideoChunked } from "../lib/uploadVideo";
import { PlatformPicker } from "../components/PlatformPicker";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { UploadCloud, X, Plus, Loader2, ShieldCheck, Lock, Users, RefreshCw, Zap, Clock, Smartphone, VolumeX, AlertTriangle } from "lucide-react";

export default function SubmitReport() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [form, setForm] = useState({ gamertag: "", platform: "", game_mode: "", cheat_type: "", description: "" });
  const [images, setImages] = useState([]);
  const [videoRows, setVideoRows] = useState([{ url: "", timestamp: "", note: "" }]);
  const [squadTags, setSquadTags] = useState([]);
  const videoFileRef = useRef(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reportType, setReportType] = useState("mod");
  const [priority, setPriority] = useState(false);
  const [idCheck, setIdCheck] = useState(null); // {loading, resolved, current_name, note}

  // Live-confirm the locked profile for Steam/Xbox as the reporter types.
  useEffect(() => {
    const handle = form.gamertag.trim();
    if (!(form.platform === "Steam" || form.platform === "Xbox" || form.platform === "PS5") || handle.length < 3) {
      setIdCheck(null);
      return;
    }
    setIdCheck({ loading: true });
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/resolve-identity", { params: { platform: form.platform, handle } });
        setIdCheck(data);
      } catch {
        setIdCheck(null);
      }
    }, 700);
    return () => clearTimeout(t);
  }, [form.gamertag, form.platform]);

  const set = (k) => (v) => setForm({ ...form, [k]: v });

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (videoFileRef.current) videoFileRef.current.value = "";
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) {
      toast.error("That clip is over 200MB. Trim it or upload a lower-resolution version.");
      return;
    }
    setVideoUploading(true);
    setVideoProgress(0);
    try {
      const url = await uploadVideoChunked(file, setVideoProgress);
      setVideoRows((rows) => {
        const empty = rows.findIndex((r) => !r.url.trim());
        const row = { url, timestamp: "", note: "" };
        if (empty >= 0) { const copy = [...rows]; copy[empty] = { ...copy[empty], url }; return copy; }
        return [...rows, row];
      });
      toast.success("Clip uploaded! It'll play right on the case.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed. Try a smaller clip.");
    } finally {
      setVideoUploading(false);
    }
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, data.url]);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.gamertag || !form.platform || !form.game_mode || !form.cheat_type || !form.description) {
      toast.error("Fill out all required fields.");
      return;
    }
    const cleanRows = videoRows
      .map((r) => ({ url: r.url.trim(), timestamp: r.timestamp.trim(), note: r.note.trim(), muted: !!r.muted }))
      .filter((r) => r.url);
    const cleanVideos = cleanRows.map((r) => r.url);
    const badLink = cleanVideos.find((v) => !videoHostAllowed(v));
    if (badLink) {
      toast.error("Video links must be from YouTube, Streamable, Twitch, Medal.tv, Imgur, Twitter/X, or Google Drive. No Discord/story links or shorteners.");
      return;
    }
    if (reportType === "community" && images.length === 0 && cleanVideos.length === 0) {
      toast.error("Community call-outs need proof — add at least one screenshot or video clip.");
      return;
    }
    setSubmitting(true);
    try {
      const squad = squadTags.map((s) => s.trim()).filter(Boolean);
      const { data: created } = await api.post("/reports", {
        ...form,
        images,
        video_links: cleanVideos,
        video_evidence: cleanRows,
        report_type: reportType,
        squad_gamertags: squad,
      });
      const madeCount = created?.squad_count || 1;
      // Priority boost: send them straight to secure checkout for this exact report (single reports only).
      if (reportType === "mod" && priority && madeCount === 1 && created?.id) {
        try {
          const { data } = await api.post("/payments/checkout", {
            lookup_key: "report_boost", origin_url: window.location.origin, report_id: created.id,
          });
          window.location.href = data.checkout_url;
          return;
        } catch {
          toast.error("Report submitted, but we couldn't start the $3 priority checkout. You can boost it from My Reports.");
          navigate("/my");
          return;
        }
      }
      if (madeCount > 1) {
        toast.success(reportType === "community"
          ? `Posted ${madeCount} suspects from your clip! Each cheater got their own card.`
          : `Submitted ${madeCount} cases from your clip — one per cheater. They're now in the modder queue.`);
      } else {
        toast.success(reportType === "community"
          ? "Posted to Suspects! The community can now upvote your call-out."
          : "Report submitted! It's now in the modder review queue.");
      }
      navigate(reportType === "community" ? "/" : "/my");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "mt-1.5 border-[#1E293F] bg-[#141B2D] text-white placeholder:text-[#64748B]";
  const trigCls = "mt-1.5 border-[#1E293F] bg-[#141B2D] text-white";

  return (
    <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
      <div className="animate-fade-up">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#00F0FF]/30 bg-[#00F0FF]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#00F0FF]">
          <ShieldCheck size={13} /> Submit Evidence
        </div>
        <h1 className="mt-4 font-heading text-3xl font-black tracking-tight text-white sm:text-4xl">Report a Cheater</h1>
        <p className="mt-2 text-[#94A3B8]">The more proof you give, the faster our modders can vet and escalate it.</p>
      </div>

      <form onSubmit={submit} className="animate-fade-up mt-8 space-y-6 rounded-2xl border border-[#1E293F] bg-[#0F1424] p-6" style={{ animationDelay: "80ms" }}>
        <div>
          <Label className="text-[#CBD5E1]">How do you want to report? *</Label>
          <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              data-testid="report-type-mod"
              onClick={() => setReportType("mod")}
              className={`rounded-xl border p-4 text-left transition-all ${reportType === "mod" ? "border-[#00F0FF] bg-[#00F0FF]/10" : "border-[#1E293F] bg-[#141B2D] hover:border-[#00F0FF]/40"}`}
            >
              <span className="flex items-center gap-2 font-heading font-bold text-white"><Lock size={15} className="text-[#00F0FF]" /> Report to Mods</span>
              <span className="mt-1 block text-xs text-[#94A3B8]">Private. Goes straight to the modder queue for review. Best for serious cases you want banned.</span>
            </button>
            <button
              type="button"
              data-testid="report-type-community"
              onClick={() => setReportType("community")}
              className={`rounded-xl border p-4 text-left transition-all ${reportType === "community" ? "border-[#F97316] bg-[#F97316]/10" : "border-[#1E293F] bg-[#141B2D] hover:border-[#F97316]/40"}`}
            >
              <span className="flex items-center gap-2 font-heading font-bold text-white"><Users size={15} className="text-[#F97316]" /> Community Call-Out</span>
              <span className="mt-1 block text-xs text-[#94A3B8]">Public in the Suspects tab so others can upvote. Proof required. Marked UNVERIFIED until a mod approves.</span>
            </button>
          </div>
        </div>

        <div>
          <Label className="text-[#CBD5E1]">Gamertag / PSN / Xbox ID *</Label>
          <Input data-testid="report-gamertag" value={form.gamertag} onChange={(e) => set("gamertag")(e.target.value)} className={inputCls} placeholder="e.g. GreenLightBandit" />
          {(form.platform === "Steam" || form.platform === "Xbox" || form.platform === "PS5") && (
            <p className="mt-1.5 flex items-start gap-1.5 rounded-lg border border-[#66C0F4]/25 bg-[#66C0F4]/[0.06] px-3 py-2 text-xs leading-relaxed text-[#94A3B8]" data-testid="identity-hint">
              <RefreshCw size={13} className="mt-px shrink-0 text-[#66C0F4]" />
              {form.platform === "Steam" ? (
                <span><span className="font-semibold text-[#CBD5E1]">Tip:</span> paste their <span className="font-semibold text-white">Steam profile link, custom URL, or SteamID</span> (not just a display name). It lets us lock their permanent ID and <span className="font-semibold text-white">auto-track if they rename</span> to dodge the wall.</span>
              ) : form.platform === "PS5" ? (
                <span><span className="font-semibold text-[#CBD5E1]">Tip:</span> enter their <span className="font-semibold text-white">exact PSN Online ID</span>. It lets us lock their permanent account ID and <span className="font-semibold text-white">auto-track if they rename</span> to dodge the wall.</span>
              ) : (
                <span><span className="font-semibold text-[#CBD5E1]">Tip:</span> enter their <span className="font-semibold text-white">exact Xbox gamertag</span>. It lets us lock their permanent ID and <span className="font-semibold text-white">auto-track if they rename</span> to dodge the wall.</span>
              )}
            </p>
          )}
          {idCheck && (
            <div className="mt-1.5" data-testid="id-check">
              {idCheck.loading ? (
                <p className="flex items-center gap-1.5 text-xs text-[#64748B]"><Loader2 size={12} className="animate-spin" /> Checking this profile…</p>
              ) : idCheck.resolved ? (
                <p className="flex items-start gap-1.5 rounded-lg border border-[#00FF87]/30 bg-[#00FF87]/[0.08] px-3 py-2 text-xs text-[#86EFAC]" data-testid="id-check-locked">
                  <ShieldCheck size={13} className="mt-px shrink-0 text-[#00FF87]" />
                  <span><span className="font-bold text-[#00FF87]">Locked ✓</span> — permanent {form.platform} ID captured{idCheck.current_name ? <>. This account currently shows as <span className="font-semibold text-white">{idCheck.current_name}</span></> : ""}. We'll auto-track any future name change.</span>
                </p>
              ) : idCheck.note ? (
                <p className="flex items-start gap-1.5 rounded-lg border border-[#FFB800]/30 bg-[#FFB800]/[0.08] px-3 py-2 text-xs text-[#FCD34D]" data-testid="id-check-fail">
                  <RefreshCw size={13} className="mt-px shrink-0 text-[#FFB800]" />
                  <span>{idCheck.note}</span>
                </p>
              ) : null}
            </div>
          )}
        </div>

        <div>
          <Label className="text-[#CBD5E1]">Platform *</Label>
          <PlatformPicker value={form.platform} onChange={set("platform")} />
        </div>

        <div data-testid="squad-section">
          <Label className="flex items-center gap-1.5 text-[#CBD5E1]"><Users size={14} className="text-[#F97316]" /> More cheaters in this clip? <span className="font-normal text-[#64748B]">(optional)</span></Label>
          <p className="mt-1 text-xs leading-relaxed text-[#94A3B8]">Caught a whole squad? Add their gamertags below — each one gets its <span className="font-semibold text-white">own case on the wall</span>, all sharing this same clip and evidence (no re-uploading). They'll use the platform, game mode and cheat type you pick here.</p>
          {squadTags.map((t, i) => (
            <div key={i} className="mt-2 flex gap-2" data-testid={`squad-row-${i}`}>
              <Input data-testid={`squad-tag-${i}`} value={t}
                onChange={(e) => setSquadTags(squadTags.map((x, idx) => (idx === i ? e.target.value : x)))}
                className="border-[#1E293F] bg-[#141B2D] text-white placeholder:text-[#64748B]" placeholder={`Cheater #${i + 2} gamertag`} />
              <Button type="button" variant="outline" size="icon" onClick={() => setSquadTags(squadTags.filter((_, idx) => idx !== i))}
                className="shrink-0 border-[#1E293F] bg-[#141B2D] text-[#94A3B8] hover:bg-[#1A233A]" data-testid={`remove-squad-${i}`}>
                <X size={15} />
              </Button>
            </div>
          ))}
          <Button type="button" variant="ghost" size="sm" onClick={() => setSquadTags([...squadTags, ""])}
            className="mt-2 text-[#F97316] hover:bg-[#F97316]/10 hover:text-[#F97316]" data-testid="add-squad-tag">
            <Plus size={15} className="mr-1" /> Add another cheater
          </Button>
        </div>

        <div>
          <Label className="text-[#CBD5E1]">Game Mode *</Label>
          <Select value={form.game_mode} onValueChange={set("game_mode")}>
            <SelectTrigger data-testid="report-mode" className={trigCls}><SelectValue placeholder="Select mode" /></SelectTrigger>
            <SelectContent className="border-[#1E293F] bg-[#141B2D] text-white">
              {GAME_MODES.map((m) => <SelectItem key={m} value={m} className="focus:bg-[#1A233A] focus:text-white">{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-[#CBD5E1]">Type of Cheat *</Label>
          <Select value={form.cheat_type} onValueChange={set("cheat_type")}>
            <SelectTrigger data-testid="report-cheat" className={trigCls}><SelectValue placeholder="Select cheat type" /></SelectTrigger>
            <SelectContent className="border-[#1E293F] bg-[#141B2D] text-white">
              {CHEAT_TYPES.map((c) => <SelectItem key={c} value={c} className="focus:bg-[#1A233A] focus:text-white">{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-[#CBD5E1]">What happened? *</Label>
          <Textarea data-testid="report-description" value={form.description} onChange={(e) => set("description")(e.target.value)} rows={4} className={inputCls} placeholder="Describe the cheating behavior, when it happened, and any details modders should know." />
        </div>

        {/* Image upload */}
        <div>
          <Label className="text-[#CBD5E1]">Photo Evidence {reportType === "community" && <span className="text-[#F97316]">(proof required)</span>}</Label>
          <div className="mt-1 flex items-start gap-2.5 rounded-lg border border-[#FFB800]/40 bg-[#FFB800]/[0.08] px-3 py-2.5 text-xs leading-relaxed text-[#FCD34D]" data-testid="stats-photo-hint">
            <AlertTriangle size={16} className="mt-px shrink-0 text-[#FFB800]" />
            <span>
              <span className="font-bold uppercase tracking-wide text-[#FFB800]">Important — add their stats:</span> There's <span className="font-semibold text-white">no public 2K API</span> to pull a player's online record, so a screenshot of their <span className="font-semibold text-white">stats across all modes</span> (REC, Park, Pro-Am…) is our <span className="font-semibold text-white">#1 way to prove cheating</span>. Their splits + build tell our mods everything. <span className="font-semibold text-white">Please include it.</span>
            </span>
          </div>
          <div
            data-testid="upload-dropzone"
            onClick={() => fileRef.current?.click()}
            className="mt-1.5 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-[#1E293F] bg-[#141B2D] py-8 text-center transition-colors hover:border-[#00F0FF]/40"
          >
            {uploading ? <Loader2 className="animate-spin text-[#00F0FF]" /> : <UploadCloud className="text-[#64748B]" size={28} />}
            <p className="mt-2 text-sm text-[#94A3B8]">Click to upload screenshots</p>
            <p className="text-xs text-[#64748B]">PNG, JPG, WEBP up to 10MB</p>
          </div>
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={handleFiles} data-testid="file-input" />
          {images.length > 0 && (
            <div className="mt-3 grid grid-cols-3 gap-2">
              {images.map((img, i) => (
                <div key={i} className="group relative overflow-hidden rounded-lg border border-[#1E293F]">
                  <img src={fileUrl(img)} alt="" className="h-20 w-full object-cover" />
                  <button type="button" onClick={() => setImages(images.filter((_, idx) => idx !== i))}
                    className="absolute right-1 top-1 rounded-full bg-black/70 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100">
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Video links */}
        <div>
          <Label className="text-[#CBD5E1]">Video Proof <span className="text-[#64748B]">(any link — YouTube, Twitch, Streamable, Twitter/X, Google Drive…)</span></Label>
          <div className="mt-1.5 space-y-3">
            <div className="rounded-xl border border-dashed border-[#00FF87]/40 bg-[#00FF87]/[0.06] p-3.5" data-testid="video-upload-box">
              <input ref={videoFileRef} type="file" accept="video/*" hidden onChange={handleVideoUpload} data-testid="video-file-input" />
              <button type="button" onClick={() => videoFileRef.current?.click()} disabled={videoUploading}
                className="flex w-full items-center gap-3 text-left" data-testid="upload-clip-btn">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#00FF87]/15 text-[#00FF87]">
                  {videoUploading ? <Loader2 size={20} className="animate-spin" /> : <Smartphone size={20} />}
                </span>
                <span className="flex-1">
                  <span className="block font-heading text-sm font-bold text-white">{videoUploading ? `Uploading… ${videoProgress}%` : "Upload a clip from your phone or PC"}</span>
                  <span className="block text-xs text-[#94A3B8]">Screen recording or camera capture — MP4, MOV, WEBM (max 200MB). Plays right on the case with jump-to-moment.</span>
                </span>
              </button>
              {videoUploading && (
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[#0F1424]" data-testid="video-upload-progress">
                  <div className="h-full rounded-full bg-[#00FF87] transition-all" style={{ width: `${videoProgress}%` }} />
                </div>
              )}
            </div>

            <div className="flex items-center gap-3" data-testid="video-or-divider">
              <div className="h-px flex-1 bg-[#1E293F]" />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">or paste a link</span>
              <div className="h-px flex-1 bg-[#1E293F]" />
            </div>

            {videoRows.map((row, i) => {
              const setRow = (patch) => setVideoRows(videoRows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
              return (
                <div key={i} className="rounded-xl border border-[#1E293F] bg-[#141B2D] p-3" data-testid={`video-row-${i}`}>
                  <div className="flex gap-2">
                    <Input
                      data-testid={`video-link-${i}`}
                      value={row.url}
                      onChange={(e) => setRow({ url: e.target.value })}
                      className="border-[#1E293F] bg-[#0F1424] text-white placeholder:text-[#64748B]"
                      placeholder="Paste any video link (YouTube, Twitter/X, Google Drive, Streamable…)"
                    />
                    {videoRows.length > 1 && (
                      <Button type="button" variant="outline" size="icon" onClick={() => setVideoRows(videoRows.filter((_, idx) => idx !== i))}
                        className="shrink-0 border-[#1E293F] bg-[#0F1424] text-[#94A3B8] hover:bg-[#1A233A]" data-testid={`remove-video-${i}`}>
                        <X size={15} />
                      </Button>
                    )}
                  </div>
                  <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-[130px_1fr]">
                    <div className="relative">
                      <Clock size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[#00F0FF]" />
                      <Input
                        data-testid={`video-timestamp-${i}`}
                        value={row.timestamp}
                        onChange={(e) => setRow({ timestamp: e.target.value })}
                        className="border-[#1E293F] bg-[#0F1424] pl-8 text-white placeholder:text-[#64748B]"
                        placeholder="0:34"
                      />
                    </div>
                    <Input
                      data-testid={`video-note-${i}`}
                      value={row.note}
                      onChange={(e) => setRow({ note: e.target.value })}
                      className="border-[#1E293F] bg-[#0F1424] text-white placeholder:text-[#64748B]"
                      placeholder="What to look for (e.g. impossible steal, 0 reaction frames)"
                    />
                  </div>
                  <label className="mt-2 flex cursor-pointer items-center gap-2 text-[11px] font-medium text-[#94A3B8]" data-testid={`video-mute-${i}`}>
                    <input type="checkbox" checked={!!row.muted} onChange={(e) => setRow({ muted: e.target.checked })} className="h-3.5 w-3.5 accent-[#00FF87]" data-testid={`video-mute-checkbox-${i}`} />
                    <VolumeX size={13} className="text-[#64748B]" /> Play this clip <span className="font-semibold text-[#CBD5E1]">muted</span> by default (even if it has sound)
                  </label>
                  <p className="mt-1.5 text-[11px] leading-snug text-[#64748B]">
                    ⏱ Add the <span className="font-semibold text-[#94A3B8]">exact time the cheat happens</span> (e.g. 0:34) so viewers jump straight to it. On YouTube we'll auto-start the clip there.
                  </p>
                </div>
              );
            })}
            <Button type="button" variant="ghost" size="sm" onClick={() => setVideoRows([...videoRows, { url: "", timestamp: "", note: "" }])}
              className="text-[#00F0FF] hover:bg-[#00F0FF]/10 hover:text-[#00F0FF]" data-testid="add-video-link">
              <Plus size={15} className="mr-1" /> Add another clip
            </Button>
          </div>

          <div className="mt-3 rounded-lg border border-[#1E293F] bg-[#0B1120] p-3 text-xs leading-relaxed" data-testid="upload-rules">
            <p className="font-semibold text-[#CBD5E1]">Clip upload rules</p>
            <p className="mt-1.5 text-[#00FF87]">✅ Accepted: YouTube, Streamable, Twitch clips, Medal.tv, Imgur, Twitter/X, Google Drive.</p>
            <p className="mt-1 text-[#FF2A55]">⛔ Not allowed: Discord links (they expire), Snapchat/Instagram stories, URL shorteners (bit.ly, grabify), and random file-host/download sites.</p>
            <p className="mt-1.5 text-[#94A3B8]">The clip must be <span className="font-semibold text-white">public (no login needed)</span>, <span className="font-semibold text-white">permanent</span>, and clearly show the cheating. For Google Drive, set sharing to <span className="font-semibold text-white">"Anyone with the link → Viewer."</span></p>
          </div>
        </div>

        {reportType === "mod" && squadTags.filter((s) => s.trim()).length === 0 && (
          <div data-testid="priority-boost-box" className="rounded-xl border border-[#FFB800]/30 bg-[#FFB800]/[0.06] p-4">
            <button
              type="button"
              data-testid="priority-boost-toggle"
              onClick={() => setPriority(!priority)}
              className="flex w-full items-start gap-3 text-left"
            >
              <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors ${priority ? "border-[#FFB800] bg-[#FFB800]" : "border-[#FFB800]/50 bg-transparent"}`}>
                {priority && <Zap size={13} className="text-[#090C15]" />}
              </span>
              <span className="flex-1">
                <span className="flex items-center gap-1.5 font-heading text-sm font-bold text-[#FFB800]">
                  <Zap size={14} /> Make this a top priority — $3
                </span>
                <span className="mt-1 block text-xs leading-relaxed text-[#94A3B8]">
                  Skip the line. Pay $3 to send this report straight to the <span className="font-semibold text-white">top of the modder review queue</span>. You'll be taken to secure checkout right after you submit. One-time — not a subscription.
                </span>
              </span>
            </button>
          </div>
        )}

        <Button data-testid="submit-report-btn" type="submit" disabled={submitting || uploading}
          className="w-full bg-[#FF2A55] font-bold text-white hover:bg-[#FF2A55]/90">
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {reportType === "community" ? "Post to Suspects" : (priority ? "Submit & Boost to Top · $3" : "Submit for Modder Review")}
        </Button>
      </form>
    </div>
  );
}
